import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "flex flex-col h-full" }
const _hoisted_2 = { class: "border-b px-4 py-2.5 flex items-center justify-between shrink-0" }
const _hoisted_3 = { class: "flex items-center gap-2 min-w-0" }
const _hoisted_4 = { class: "flex items-center gap-2 shrink-0" }
const _hoisted_5 = { class: "flex flex-col gap-0.5 py-0.5" }
const _hoisted_6 = { class: "text-sm leading-none" }
const _hoisted_7 = { class: "text-[11px] leading-none text-muted-foreground" }
const _hoisted_8 = {
  key: 0,
  class: "flex-1 flex items-center justify-center p-8"
}
const _hoisted_9 = { class: "text-center space-y-3" }
const _hoisted_10 = {
  key: 1,
  class: "flex-1 overflow-auto"
}
const _hoisted_11 = { class: "p-4 text-center space-y-2" }
const _hoisted_12 = { class: "p-4 space-y-4" }
const _hoisted_13 = {
  key: 0,
  class: "flex gap-3 max-w-[85%]"
}
const _hoisted_14 = { class: "h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5" }
const _hoisted_15 = { class: "bg-muted rounded-lg px-3 py-2 min-w-0" }
const _hoisted_16 = {
  key: 1,
  class: "max-w-[75%]"
}
const _hoisted_17 = { class: "bg-primary text-primary-foreground rounded-lg px-3 py-2" }
const _hoisted_18 = { class: "text-sm whitespace-pre-wrap" }
const _hoisted_19 = {
  key: 0,
  class: "flex gap-3"
}
const _hoisted_20 = { class: "h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0" }
const _hoisted_21 = {
  key: 3,
  class: "px-4 py-2 bg-destructive/10 text-destructive text-xs"
}
const _hoisted_22 = {
  key: 4,
  class: "border-t p-3 space-y-2 shrink-0"
}
const _hoisted_23 = { class: "flex gap-2" }

import { ref, computed, nextTick, watch, inject } from 'vue'
import { storeToRefs } from 'pinia'
import {
  BotMessageSquare,
  Send,
  FolderKanban,
} from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { MdPreview } from 'md-editor-v3'
import 'md-editor-v3/lib/preview.css'
import { useCopilotStore } from '../store'
import type { useCopilotChat } from '../composables/useCopilotChat'
import TemplateSelector from './TemplateSelector.vue'
import type { CopilotTemplate } from '@/types'


export default /*@__PURE__*/_defineComponent({
  __name: 'CopilotChatPanel',
  setup(__props) {

const copilotStore = useCopilotStore()
const { projectContext, selectedItemIds } = storeToRefs(copilotStore)

const chat = inject<ReturnType<typeof useCopilotChat>>('copilotChat')!
const {
  sessionId,
  messages,
  isSending,
  error,
  areProfilesLoading,
  selectedProfile,
  profileOptions,
  hasMessages,
  initSession,
  sendMessage,
  setSelectedProfile,
} = chat

const userInput = ref('')
const messagesContainer = ref<HTMLElement | null>(null)

const selectedCount = computed(() => selectedItemIds.value.length)

const hasProject = computed(() => !!projectContext.value)
const selectedProfileValue = computed<string>({
  get: () => selectedProfile.value || 'all',
  set: (value) => setSelectedProfile(value),
})
const selectedProfileLabel = computed(() => {
  return profileOptions.value.find((option) => option.value === selectedProfileValue.value)?.label
    || 'General (todas las tools)'
})

const scrollToBottom = async () => {
  await nextTick()
  if (messagesContainer.value) {
    const scrollEl = messagesContainer.value.querySelector('[data-radix-scroll-area-viewport]')
    if (scrollEl) {
      scrollEl.scrollTop = scrollEl.scrollHeight
    }
  }
}

watch(messages, () => {
  scrollToBottom()
}, { deep: true })

const handleSend = async () => {
  if (!userInput.value.trim() || !hasProject.value || isSending.value) return

  if (!sessionId.value) {
    initSession()
  }

  const context = copilotStore.buildContextPayload()
  if (!context) return

  const msg = userInput.value
  userInput.value = ''

  await sendMessage(msg, context)
}

const handleKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Enter' && !event.shiftKey) {
    event.preventDefault()
    handleSend()
  }
}

const handleTemplateSelect = async (template: CopilotTemplate) => {
  copilotStore.setTemplate(template)

  if (template.promptTemplate) {
    if (!sessionId.value) {
      initSession()
    }

    const context = copilotStore.buildContextPayload()
    if (!context) return

    userInput.value = ''
    await sendMessage(template.promptTemplate, {
      ...context,
      templatePrompt: template.promptTemplate,
    })
  }
}



return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createVNode(_unref(BotMessageSquare), { class: "h-4 w-4" }),
        _cache[2] || (_cache[2] = _createElementVNode("span", { class: "text-sm font-medium" }, "Chat", -1)),
        (selectedCount.value > 0)
          ? (_openBlock(), _createBlock(_unref(Badge), {
              key: 0,
              variant: "secondary",
              class: "text-xs"
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(selectedCount.value) + " items en contexto ", 1)
              ]),
              _: 1
            }))
          : _createCommentVNode("", true)
      ]),
      _createElementVNode("div", _hoisted_4, [
        _createVNode(_unref(Select), {
          modelValue: selectedProfileValue.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((selectedProfileValue).value = $event)),
          disabled: _unref(areProfilesLoading)
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(SelectTrigger), { class: "h-8 w-[190px] text-xs" }, {
              default: _withCtx(() => [
                _createVNode(_unref(SelectValue), { placeholder: "Rol" }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(selectedProfileLabel.value), 1)
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }),
            _createVNode(_unref(SelectContent), {
              align: "end",
              class: "w-[230px]"
            }, {
              default: _withCtx(() => [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(profileOptions), (option) => {
                  return (_openBlock(), _createBlock(_unref(SelectItem), {
                    key: option.value,
                    value: option.value
                  }, {
                    default: _withCtx(() => [
                      _createElementVNode("div", _hoisted_5, [
                        _createElementVNode("span", _hoisted_6, _toDisplayString(option.label), 1),
                        _createElementVNode("span", _hoisted_7, _toDisplayString(option.description), 1)
                      ])
                    ]),
                    _: 2
                  }, 1032, ["value"]))
                }), 128))
              ]),
              _: 1
            })
          ]),
          _: 1
        }, 8, ["modelValue", "disabled"])
      ])
    ]),
    (!hasProject.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
          _createElementVNode("div", _hoisted_9, [
            _createVNode(_unref(FolderKanban), { class: "h-12 w-12 mx-auto text-muted-foreground/40" }),
            _cache[3] || (_cache[3] = _createElementVNode("div", null, [
              _createElementVNode("p", { class: "text-sm font-medium" }, "Selecciona un proyecto"),
              _createElementVNode("p", { class: "text-xs text-muted-foreground mt-1" }, " Elige un proyecto en el panel izquierdo para comenzar ")
            ], -1))
          ])
        ]))
      : (!_unref(hasMessages))
        ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
            _createElementVNode("div", _hoisted_11, [
              _createVNode(_unref(BotMessageSquare), { class: "h-8 w-8 mx-auto text-muted-foreground/40" }),
              _cache[4] || (_cache[4] = _createElementVNode("p", { class: "text-sm font-medium" }, "Elige un template o escribe tu consulta", -1)),
              _cache[5] || (_cache[5] = _createElementVNode("p", { class: "text-xs text-muted-foreground" }, " Los templates te ayudan a generar documentos especificos ", -1))
            ]),
            _createVNode(TemplateSelector, { onSelect: handleTemplateSelect })
          ]))
        : (_openBlock(), _createElementBlock("div", {
            key: 2,
            class: "flex-1 min-h-0",
            ref_key: "messagesContainer",
            ref: messagesContainer
          }, [
            _createVNode(_unref(ScrollArea), { class: "h-full" }, {
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_12, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(messages), (message) => {
                    return (_openBlock(), _createElementBlock("div", {
                      key: message.id,
                      class: _normalizeClass(["flex gap-3", { 'justify-end': message.role === 'user' }])
                    }, [
                      (message.role === 'assistant')
                        ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                            _createElementVNode("div", _hoisted_14, [
                              _createVNode(_unref(BotMessageSquare), { class: "h-4 w-4 text-primary" })
                            ]),
                            _createElementVNode("div", _hoisted_15, [
                              _createVNode(_unref(MdPreview), {
                                "model-value": message.content,
                                language: "en-US",
                                theme: 'light',
                                class: "copilot-preview"
                              }, null, 8, ["model-value"])
                            ])
                          ]))
                        : (message.role === 'user')
                          ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
                              _createElementVNode("div", _hoisted_17, [
                                _createElementVNode("p", _hoisted_18, _toDisplayString(message.content), 1)
                              ])
                            ]))
                          : _createCommentVNode("", true)
                    ], 2))
                  }), 128)),
                  (_unref(isSending))
                    ? (_openBlock(), _createElementBlock("div", _hoisted_19, [
                        _createElementVNode("div", _hoisted_20, [
                          _createVNode(_unref(BotMessageSquare), { class: "h-4 w-4 text-primary" })
                        ]),
                        _cache[6] || (_cache[6] = _createElementVNode("div", { class: "bg-muted rounded-lg px-4 py-3" }, [
                          _createElementVNode("div", { class: "flex gap-1" }, [
                            _createElementVNode("div", {
                              class: "h-2 w-2 rounded-full bg-muted-foreground/50 animate-bounce",
                              style: {"animation-delay":"0ms"}
                            }),
                            _createElementVNode("div", {
                              class: "h-2 w-2 rounded-full bg-muted-foreground/50 animate-bounce",
                              style: {"animation-delay":"150ms"}
                            }),
                            _createElementVNode("div", {
                              class: "h-2 w-2 rounded-full bg-muted-foreground/50 animate-bounce",
                              style: {"animation-delay":"300ms"}
                            })
                          ])
                        ], -1))
                      ]))
                    : _createCommentVNode("", true)
                ])
              ]),
              _: 1
            })
          ], 512)),
    (_unref(error))
      ? (_openBlock(), _createElementBlock("div", _hoisted_21, _toDisplayString(_unref(error)), 1))
      : _createCommentVNode("", true),
    (hasProject.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_22, [
          _createElementVNode("div", _hoisted_23, [
            _createVNode(_unref(Textarea), {
              modelValue: userInput.value,
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((userInput).value = $event)),
              placeholder: "Escribe tu consulta al copiloto...",
              class: "min-h-[40px] max-h-[120px] resize-none text-sm",
              rows: "1",
              disabled: _unref(isSending),
              onKeydown: handleKeydown
            }, null, 8, ["modelValue", "disabled"]),
            _createVNode(_unref(Button), {
              size: "icon",
              class: "shrink-0 h-10 w-10",
              disabled: !userInput.value.trim() || _unref(isSending),
              onClick: handleSend
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(Send), { class: "h-4 w-4" })
              ]),
              _: 1
            }, 8, ["disabled"])
          ]),
          _cache[7] || (_cache[7] = _createElementVNode("p", { class: "text-[10px] text-muted-foreground" }, " Enter para enviar, Shift+Enter nueva linea ", -1))
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})